import { AddRounded, ContentCopy, Edit, EditRounded } from '@mui/icons-material'
import { Box, Card, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import ApiClient from '../../backend/ApiClient'
import PermissionManager from '../../managers/PermissionManager'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { modals } from '../services/ModalService'

const groupManagement = () => {
    const dispatch = useAppDispatch()
    const invalidateAllGroups = useInvalidate({ queryKey: buildQueryKey('get-approvals-groups') })
    const theme = useTheme()
    const { status, response: groups } = useQuery({
        queryKey: buildQueryKey('get-approvals-groups'),
        queryFn: () => ApiClient.getApprovalGroups(),
        cache: true
    })

    if (status === 'loading') {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' height='100vh'>
                <CircularProgress size={100} />
            </Box>
        )
    }

    return (
        <SmartCard>
            <Stack spacing={2}>
                <Stack spacing={2} direction='row'>
                    <Edit fontSize='large' />
                    <Typography variant='h5' fontWeight='bold'>
                        Groups Editor
                    </Typography>
                </Stack>
                <Divider />
                <Box
                    display='grid'
                    gridTemplateColumns='repeat(auto-fill, minmax(300px, 1fr))'
                    gap={2}
                    sx={{
                        maxHeight: 'calc(100vh - 200px)',
                        overflowY: 'auto'
                    }}>
                    {groups?.map((group, index) => (
                        <Card
                            key={index}
                            sx={{
                                height: '250px',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                boxShadow: 1,
                                borderRadius: 2,
                                border: group.isDefault ? `2px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`

                            }}>
                            <Stack spacing={2} flex={1}>
                                <Box display='flex' justifyContent='space-between' alignItems='center'>
                                    <Typography
                                        variant='h6'
                                        fontWeight='bold'
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0
                                        }}>
                                        {group.name}
                                    </Typography>
                                    <Stack direction='row' spacing={0.5}>
                                        <Tooltip title='Clone' arrow>
                                            <IconButton
                                                onClick={async () => {
                                                    modals.openConfirm({
                                                        title: 'Confirm Group Deletion',
                                                        children: <p>Are you sure you want to clone this Group?</p>,
                                                        labels: { confirm: 'Yes, Clone', cancel: 'Cancel' },
                                                        colors: { confirm: 'info', cancel: 'primary' },
                                                        onConfirm: async () => {
                                                            await PermissionManager.createGroup(
                                                                `${group.name} (copy)`,
                                                                group.description,
                                                                group.isDefault,
                                                                group.entitlements.allow,
                                                                group.entitlements.disallow
                                                            )
                                                            await invalidateAllGroups()
                                                            dispatch(changeRoute({
                                                                route: 'groups-management'
                                                            }))
                                                        }
                                                    })
                                                }}
                                                color='inherit'>
                                                <ContentCopy />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Edit' arrow>
                                            <IconButton
                                                disabled={group.isDefault}
                                                color='inherit'
                                                onClick={() => {
                                                    dispatch(changeRoute({
                                                        route: 'groups-editor',
                                                        params: { roleUuid: group.uuid, isClone: false } }))
                                                }}>
                                                <EditRounded />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                                <Divider />
                                <Stack justifyContent='space-between' flex={1}>
                                    <Stack overflow='hidden'>
                                        <Typography
                                            display='-webkit-box'
                                            variant='body1'
                                            color='textSecondary'
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical' }}>
                                            {group.description === '' ? `group for ${group.name}` : group.description}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Card>
                    ))}
                    <Card
                        sx={{
                            p: 2,
                            height: '250px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid var(--primary-_states-outlinedBorder, #008ECB80)',
                            boxShadow: 'none',
                            borderRadius: 2,
                            cursor: 'pointer',
                            background: 'var(--secondary-_states-focus, #26C9FF1F)'
                        }}
                        onClick={() => {
                            dispatch(changeRoute({ route: 'groups-editor' }))
                        }}>
                        <Stack>
                            <Stack
                                alignItems='center'>
                                <AddRounded color='primary' />
                            </Stack>
                            <Typography
                                variant='h6'
                                color='primary'
                                alignItems='center'>
                                New Groups
                            </Typography>
                        </Stack>
                    </Card>
                </Box>
            </Stack>
        </SmartCard>

    )
}

export default groupManagement
